import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, AED, AED_short, fileUrl } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ArrowLeft, Upload, Sparkles, Loader2, FileText, Image as ImageIcon, Plus, Trash2, CheckCircle, AlertTriangle, ExternalLink, Receipt } from 'lucide-react';
import InvoiceModal from '../components/InvoiceModal';
import MapView from '../components/MapView';
import AIRefiner from '../components/AIRefiner';
import BOQUploadModal from '../components/BOQUploadModal';
import RentPropertyModal from '../components/RentPropertyModal';
import VendorLineReconModal from '../components/VendorLineReconModal';
import { motion } from 'framer-motion';

function sourceBadge(url) {
  if (!url) return null;
  try {
    const h = new URL(url).hostname.replace('www.', '');
    if (h.includes('dubailand')) return 'DLD';
    if (h.includes('propertymonitor')) return 'Property Monitor';
    if (h.includes('reidin')) return 'Reidin';
    if (h.includes('bayut')) return 'Bayut';
    if (h.includes('propertyfinder')) return 'PF';
    if (h.includes('dubizzle')) return 'Dubizzle';
    return h.split('.')[0];
  } catch { return null; }
}

const TABS = ['Overview', 'BOQ & Vendors', 'Accounts', 'Drawings & Permits', 'Documents', 'Photos', 'Snags', 'Sales'];

function Tab({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm transition-all ${
        active ? 'bg-[#1A2530] text-white shadow-md' : 'text-slate-600 hover:bg-white/60'
      }`}>{children}</button>
  );
}

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [tab, setTab] = useState('Overview');
  const [p, setP] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [boqs, setBoqs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [permits, setPermits] = useState([]);
  const [docs, setDocs] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [snags, setSnags] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [recon, setRecon] = useState(null);
  const [yieldData, setYieldData] = useState(null);
  const [rentOpen, setRentOpen] = useState(false);
  const [lineRecon, setLineRecon] = useState(null); // { vendorId, vendorName }
  const [aiLoading, setAiLoading] = useState(false);
  const [viewInv, setViewInv] = useState(null);
  const [boqUploadOpen, setBoqUploadOpen] = useState(false);
  const [markingId, setMarkingId] = useState(null);

  const load = async () => {
    const [pr, pn, bq, iv, dr, pm, dc, by, pt, sn, ex, vd, rc, yl] = await Promise.all([
      api.get(`/properties/${id}`),
      api.get(`/dashboard/pnl/${id}`).catch(() => ({ data: null })),
      api.get('/boqs', { params: { property_id: id } }),
      api.get('/invoices', { params: { property_id: id } }),
      api.get('/drawings', { params: { property_id: id } }),
      api.get('/permits', { params: { property_id: id } }),
      api.get('/docs', { params: { property_id: id } }),
      api.get('/buyers', { params: { property_id: id } }),
      api.get('/partners', { params: { property_id: id } }),
      api.get('/snags', { params: { property_id: id } }),
      api.get('/expenses', { params: { property_id: id } }),
      api.get('/vendors'),
      api.get(`/properties/${id}/boq-reconciliation`).catch(() => ({ data: null })),
      api.get(`/properties/${id}/yield`).catch(() => ({ data: null })),
    ]);
    setP(pr.data); setPnl(pn.data); setBoqs(bq.data); setInvoices(iv.data);
    setDrawings(dr.data); setPermits(pm.data); setDocs(dc.data); setBuyers(by.data);
    setPartners(pt.data); setSnags(sn.data); setExpenses(ex.data);
    setVendors(vd.data); setRecon(rc.data); setYieldData(yl.data);
    setPhotos(dc.data.filter(d => d.category === 'progress_photo' || d.category === 'before' || d.category === 'after'));
  };
  useEffect(() => { load(); }, [id]);

  // Auto-open the "Mark as rented" modal when navigated with ?openRent=1
  // (drag-drop into Rented column from Properties page routes here).
  useEffect(() => {
    if (p && searchParams.get('openRent') === '1') {
      setRentOpen(true);
    }
  }, [p, searchParams]);

  const toggleFinal = async (boq) => {
    setMarkingId(boq.id);
    try {
      await api.post(`/boqs/${boq.id}/${boq.is_final ? 'unmark-final' : 'mark-final'}`);
      await load();
    } finally { setMarkingId(null); }
  };

  const editNegotiatedTotal = async (boq) => {
    const current = boq.negotiated_total_aed != null
      ? String(boq.negotiated_total_aed)
      : (boq.line_sum_aed != null ? String(boq.line_sum_aed) : String(boq.total_aed || ''));
    const raw = window.prompt(
      `Set negotiated total for "${boq.title}".\n\nFile sum: ${boq.line_sum_aed != null ? boq.line_sum_aed.toLocaleString() : (boq.total_aed || 0).toLocaleString()} AED\n\n` +
      `Enter the agreed total AED, or leave blank / 0 to revert to file sum.`,
      current
    );
    if (raw === null) return;
    const val = raw.trim() === '' ? null : parseFloat(raw);
    try {
      await api.post(`/boqs/${boq.id}/set-negotiated-total`, {
        negotiated_total_aed: (val && val > 0) ? val : null,
      });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to update total');
    }
  };

  const [analyzingBoqId, setAnalyzingBoqId] = useState(null);
  const analyzeBoq = async (boq) => {
    setAnalyzingBoqId(boq.id);
    try {
      await api.post(`/boqs/${boq.id}/analyze`,
                      { extract_lines: true, run_audit: true });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'AI analysis failed. Check LLM credits + try again.');
    } finally { setAnalyzingBoqId(null); }
  };

  const deleteBoq = async (boq) => {
    if (!window.confirm(`Delete BOQ "${boq.title}"?\n\nThis removes the BOQ + its line items. Invoices raised against it stay untouched, but any line-level allocations will be orphaned. This can't be undone.`)) return;
    try {
      await api.delete(`/boqs/${boq.id}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to delete BOQ');
    }
  };

  const runAiAnalyze = async () => {
    setAiLoading(true);
    try {
      await api.post('/deal/analyze', { ...p, id: p.id });
      await load();
    } finally { setAiLoading(false); }
  };

  const [aiRefining, setAiRefining] = useState(false);
  const refineAiWithAnswers = async (qaPairs) => {
    setAiRefining(true);
    try {
      const merged = [...(p.ai_qa_pairs || []), ...qaPairs];
      await api.post('/deal/analyze', { ...p, id: p.id, qa_pairs: merged });
      // persist qa history on property so it's not lost
      await api.patch(`/properties/${p.id}`, { ai_qa_pairs: merged });
      await load();
    } finally { setAiRefining(false); }
  };

  const uploadDoc = async (e, category) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const form = new FormData();
    form.append('file', f);
    form.append('property_id', id);
    form.append('category', category || 'general');
    form.append('title', f.name);
    await api.post('/files/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    load();
  };

  if (!p) return <div className="p-10">Loading…</div>;

  const canDelete = !!user && (
    user.role === 'owner' || user.role === 'md' ||
    (p.created_by && p.created_by === user.user_id)
  );

  const handleDelete = async () => {
    const confirmMsg = `Delete "${p.title}"?\n\nThis will remove the property and its metadata. `
      + `Invoices, BOQs, snags and other linked records will remain in the database.\n\nThis cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.delete(`/properties/${id}`);
      navigate('/app/properties');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="py-6 max-w-[1300px] mx-auto space-y-6" data-testid="property-detail-page">
      <div className="flex items-center justify-between mb-2">
        <Link to="/app/properties" className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1">
          <ArrowLeft size={14}/> Back to pipeline
        </Link>
        {canDelete && (
          <button
            data-testid="property-delete-btn"
            onClick={handleDelete}
            className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full px-3 py-1.5 flex items-center gap-1.5 transition-all"
            title={p.created_by === user?.user_id && user?.role !== 'owner' && user?.role !== 'md'
              ? 'You created this property — you can delete your own draft'
              : 'Delete this property'}
          >
            <Trash2 size={14}/> Delete property
          </button>
        )}
      </div>

      {/* Hero */}
      <div className="glass overflow-hidden">
        <div className="h-64 bg-slate-200 relative">
          {p.hero_image_url && <img src={p.hero_image_url} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-6 text-white">
            <div className="overline text-white/70">{p.community} · {p.building}</div>
            <h1 className="font-serif text-4xl mt-1">{p.title}</h1>
            <div className="flex gap-4 mt-3 text-sm text-white/90 items-center">
              <span>{p.bedrooms} BR</span>
              <span>·</span>
              <span>{p.bathrooms} Bath</span>
              <span>·</span>
              <span>{p.size_sqft} sqft</span>
              <span>·</span>
              <select value={p.stage}
                onChange={async (e) => {
                  const newStage = e.target.value;
                  if (newStage === 'rented') {
                    // Reset select back so the modal's success handler is the
                    // single source of truth for the transition.
                    e.target.value = p.stage;
                    setRentOpen(true);
                    return;
                  }
                  await api.post(`/properties/${id}/stage`, { stage: newStage });
                  load();
                }}
                data-testid="property-stage-select"
                className="bg-white/20 backdrop-blur border border-white/40 rounded-full px-3 py-1 text-white text-xs cursor-pointer">
                <option value="sourcing" className="text-slate-900">Sourcing</option>
                <option value="under_offer" className="text-slate-900">Under Offer</option>
                <option value="owned" className="text-slate-900">Owned</option>
                <option value="refurb" className="text-slate-900">Refurbishing</option>
                <option value="listed" className="text-slate-900">Listed</option>
                <option value="sold" className="text-slate-900">Sold</option>
                <option value="rented" className="text-slate-900">Rented</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 glass p-2">
        {TABS.map(t => <Tab key={t} testid={`tab-${t.toLowerCase().replace(/[^a-z]+/g,'-')}`} active={tab === t} onClick={() => setTab(t)}>{t}</Tab>)}
      </div>

      {tab === 'Overview' && (
        <div className="space-y-5">
          {/* Rental snapshot + yield — surfaces when the property has an
              annual rent captured, whether stage=rented or a "hold-only"
              intent while still in listed/owned. */}
          {yieldData && yieldData.has_data && (
            <div className="glass p-6" data-testid="rental-yield-card">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div>
                  <div className="overline">Rental performance</div>
                  <div className="font-serif text-2xl mt-1">
                    {p.tenant_name ? <>Rented to {p.tenant_name}</> : 'Yield estimate'}
                  </div>
                  {p.lease_start_date && p.lease_end_date && (
                    <div className="text-xs text-slate-500 mt-1">
                      Lease {p.lease_start_date} → {p.lease_end_date}
                    </div>
                  )}
                </div>
                {p.stage !== 'rented' && (
                  <button onClick={() => setRentOpen(true)}
                           data-testid="mark-rented-btn"
                           className="btn-primary text-sm bg-teal-600 hover:bg-teal-700 inline-flex items-center gap-2">
                    Mark as rented
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <YieldTile label="Gross yield" value={yieldData.gross_yield_pct != null ? `${yieldData.gross_yield_pct}%` : '—'}
                            accent="text-slate-900"
                            hint={`${AED_short(yieldData.annual_rent_aed)} annual`}
                            testid="yield-gross"/>
                <YieldTile label="Net yield" value={yieldData.net_yield_pct != null ? `${yieldData.net_yield_pct}%` : '—'}
                            accent="text-teal-700"
                            hint={`After ${yieldData.management_fee_pct}% mgmt${yieldData.service_charge_aed_pa > 0 ? ' + service' : ''}`}
                            testid="yield-net"/>
                <YieldTile label="Annual rent" value={AED_short(yieldData.annual_rent_aed)}
                            accent="text-slate-900"
                            hint={`≈ ${AED_short(yieldData.monthly_rent_aed)}/month`}
                            testid="yield-annual-rent"/>
                <YieldTile label="All-in cost" value={AED_short(yieldData.all_in_cost_aed)}
                            accent="text-slate-900"
                            hint={`Refurb ${yieldData.refurb_source} · DLD included`}
                            testid="yield-all-in"/>
              </div>
            </div>
          )}

          {/* CTA to open the Mark-as-rented modal even when there's no rent
              data yet (e.g. property in Listed / Owned). */}
          {(!yieldData || !yieldData.has_data) && ['listed', 'owned'].includes(p.stage) && (
            <div className="glass p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-slate-600">
                Considering a hold-and-rent exit? Capture tenant + lease + rent to unlock yield tracking.
              </div>
              <button onClick={() => setRentOpen(true)}
                       data-testid="mark-rented-btn"
                       className="btn-primary text-sm bg-teal-600 hover:bg-teal-700">
                Mark as rented
              </button>
            </div>
          )}

          <div className="glass p-6 space-y-4">
            <div className="overline">Financials</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div><div className="text-xs text-slate-500">Purchase</div><div className="font-serif text-2xl">{AED_short(p.purchase_price_aed)}</div></div>
              <div><div className="text-xs text-slate-500">Fair (Iris AI)</div><div className="font-serif text-2xl">{AED_short(p.fair_price_aed)}</div></div>
              <div><div className="text-xs text-slate-500">Sale value after refurb</div><div className="font-serif text-2xl">{AED_short(p.arv_aed)}</div></div>
              <div><div className="text-xs text-slate-500">Refurb budget</div><div className="font-serif text-2xl">{AED_short(p.refurb_budget_aed)}</div></div>
              <div><div className="text-xs text-slate-500">Refurb spent</div><div className="font-serif text-2xl">{AED_short(p.refurb_actual_aed)}</div></div>
              <div><div className="text-xs text-slate-500">Deal score</div><div className="font-serif text-2xl text-[#679286]">{p.deal_score ?? '—'}</div></div>
            </div>
            {pnl && (
              <div className="mt-6 pt-4 border-t border-slate-200/60">
                <div className="overline mb-3">Live P&L</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-slate-500">DLD (4%):</span> {AED_short(pnl.dld_fees)}</div>
                  <div><span className="text-slate-500">Agent (2%):</span> {AED_short(pnl.agent_fees)}</div>
                  <div><span className="text-slate-500">Permits:</span> {AED_short(pnl.permit_fees)}</div>
                  <div><span className="text-slate-500">Holding:</span> {AED_short(pnl.holding)}</div>
                  <div className="col-span-2"><span className="text-slate-500">Total cost:</span> <span className="font-semibold">{AED(pnl.total_cost)}</span></div>
                  <div className="col-span-2"><span className="text-slate-500">Projected profit:</span> <span className={`font-semibold ${pnl.projected_profit > 0 ? 'text-emerald-700' : 'text-red-600'}`}>{AED(pnl.projected_profit)} ({pnl.roi_pct?.toFixed(1)}% ROI)</span></div>
                </div>
              </div>
            )}
          </div>

          <div className="glass p-8" data-testid="ai-analysis-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline">Iris AI · Full analysis</div>
                <div className="font-serif text-2xl mt-1">AI valuation & guidance</div>
              </div>
              <button onClick={runAiAnalyze} disabled={aiLoading} className="btn-gold text-sm flex items-center gap-1.5">
                {aiLoading ? <><Loader2 size={14} className="animate-spin"/> Analyzing…</> : <><Sparkles size={14}/> Re-analyze</>}
              </button>
            </div>
            {p.ai_analysis ? (
              <div className="space-y-5 text-sm">
                {p.ai_analysis.summary && <div className="p-4 rounded-2xl bg-white/60 border border-white/70 text-base text-slate-800 leading-relaxed">{p.ai_analysis.summary}</div>}

                {p.ai_analysis.clarifying_questions?.length > 0 && (
                  <AIRefiner
                    questions={p.ai_analysis.clarifying_questions}
                    onRefine={refineAiWithAnswers}
                    refining={aiRefining}
                    refinedCount={(p.ai_qa_pairs || []).length > 0 ? 1 : 0}
                  />
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {p.ai_analysis.positives?.length > 0 && <div><div className="text-xs font-medium text-emerald-700 mb-1">Positives</div><ul className="text-xs text-slate-600 list-disc pl-4 space-y-1">{p.ai_analysis.positives.map((x,i)=><li key={i}>{x}</li>)}</ul></div>}
                  {p.ai_analysis.risks?.length > 0 && <div><div className="text-xs font-medium text-red-700 mb-1">Risks</div><ul className="text-xs text-slate-600 list-disc pl-4 space-y-1">{p.ai_analysis.risks.map((x,i)=><li key={i}>{x}</li>)}</ul></div>}
                  {p.ai_analysis.premium_factors?.length > 0 && <div><div className="text-xs font-medium text-[#679286] mb-1">Premium factors</div><ul className="text-xs text-slate-600 list-disc pl-4 space-y-1">{p.ai_analysis.premium_factors.map((x,i)=><li key={i}>{x}</li>)}</ul></div>}
                </div>
                {p.ai_analysis.comparables?.length > 0 && <div>
                  <div className="text-xs font-medium text-slate-700 mb-2">Recent sold comparables <span className="text-slate-400 font-normal">— click to open the permanent sale-record source</span></div>
                  <div className="space-y-1">{p.ai_analysis.comparables.map((c,i)=>{
                    const href = c.source_url || c.listing_url;
                    const badge = c.source_name || sourceBadge(c.source_url);
                    return (
                      <a key={i} href={href || '#'} target="_blank" rel="noreferrer"
                         className="flex justify-between items-center p-2 bg-white/50 hover:bg-white rounded-xl text-xs group"
                         data-testid={`pd-comparable-${i}`}>
                        <span className="truncate flex-1 pr-2">{c.description}</span>
                        {badge && <span className="px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wider font-semibold mr-2">{badge}</span>}
                        <span className="flex items-center gap-1">{AED_short(c.price_aed)}{href && <ExternalLink size={11} className="text-slate-400 group-hover:text-[#679286]"/>}</span>
                      </a>
                    );
                  })}</div>
                </div>}
                {p.ai_analysis.sources?.length > 0 && <div className="pt-3 border-t border-slate-200/60">
                  <div className="text-xs font-medium text-slate-700 mb-2">Sources</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">{p.ai_analysis.sources.map((s,i)=>(
                    <a key={i} href={s.url} target="_blank" rel="noreferrer" className="p-2 bg-white/50 hover:bg-white rounded-xl text-xs flex justify-between">
                      <span className="truncate">{s.title}</span><span className="text-[#679286]">↗</span>
                    </a>
                  ))}</div>
                </div>}
              </div>
            ) : (
              <div className="text-sm text-slate-500">Click "Re-analyze" to get a fresh Iris AI valuation with sources & guidance.</div>
            )}
          </div>

          {p.notes && <div className="glass p-6">
            <div className="overline mb-2">Notes</div>
            <div className="text-sm text-slate-700">{p.notes}</div>
          </div>}
          <MapView query={`${p.building || ''} ${p.community}`} height={340}/>
        </div>
      )}

      {tab === 'BOQ & Vendors' && (
        <div className="space-y-6">
          {/* Project cost reconciliation summary */}
          {recon && recon.project && (
            <div className="glass p-6">
              <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div>
                  <div className="overline">Project cost reconciliation</div>
                  <div className="font-serif text-2xl">Budget vs Actuals</div>
                </div>
                <div className="text-xs text-slate-500">
                  {recon.project.vendors_finalised}/{recon.project.vendor_count} vendors finalised
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <ReconTile label="Final budget" value={AED_short(recon.project.final_budget_aed)} accent="text-slate-900"
                            hint="Sum of finalised BOQs across all vendors"/>
                <ReconTile label="Invoiced so far"
                            value={AED_short(recon.project.invoiced_total_aed)} accent="text-blue-700"
                            hint={recon.project.invoiced_pct != null
                              ? `${recon.project.invoiced_pct}% of budget · ${AED_short(recon.project.paid_total_aed)} paid · ${AED_short(recon.project.awaiting_payment_aed)} awaiting`
                              : `${AED_short(recon.project.paid_total_aed)} paid · ${AED_short(recon.project.awaiting_payment_aed)} awaiting pay`}/>
                <ReconTile label="Budget remaining" value={AED_short(recon.project.budget_remaining_aed)} accent="text-slate-900"
                            hint={recon.project.remaining_pct != null
                              ? `${recon.project.remaining_pct}% left to invoice`
                              : 'Final budget − Invoiced'}/>
                <ReconTile label={recon.project.variance_aed >= 0 ? 'Under budget by' : 'Over budget by'}
                            value={AED_short(Math.abs(recon.project.variance_aed))}
                            accent={recon.project.variance_aed >= 0 ? 'text-emerald-700' : 'text-red-600'}
                            hint={recon.project.variance_pct != null ? `${recon.project.variance_pct}% of budget` : ''}/>
              </div>
              {recon.per_vendor?.length > 0 && (
                <div className="mt-6">
                  <div className="overline mb-2">Per-vendor breakdown</div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-slate-500 border-b border-slate-200/60">
                        <th className="py-2 text-left">Vendor</th>
                        <th className="text-right">Final BOQ</th>
                        <th className="text-right">Invoiced</th>
                        <th className="text-right">Paid</th>
                        <th className="text-right" title="Invoiced but not yet paid">Awaiting pay</th>
                        <th className="text-right" title="Final BOQ − Invoiced">Budget left</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recon.per_vendor.map(r => (
                        <tr key={r.vendor_id}
                             onClick={() => setLineRecon({ vendorId: r.vendor_id, vendorName: r.vendor_name })}
                             className="border-b border-slate-100/70 cursor-pointer hover:bg-white/50"
                             data-testid={`recon-row-${r.vendor_id}`}
                             title="View line-level reconciliation">
                          <td className="py-2">
                            <div className="font-medium">{r.vendor_name}</div>
                            <div className="text-xs text-slate-500">{r.boq_count} BOQ{r.boq_count === 1 ? '' : 's'}{r.final_boq_id ? ' · finalised' : ' · no final yet'}<span className="ml-2 text-[#679286]">· drill down →</span></div>
                          </td>
                          <td className="text-right">{r.final_boq_total ? AED_short(r.final_boq_total) : <span className="text-slate-400">—</span>}</td>
                          <td className="text-right">{AED_short(r.invoiced_total)}</td>
                          <td className="text-right text-emerald-700">{AED_short(r.paid_total)}</td>
                          <td className="text-right text-amber-700">{AED_short(r.awaiting_payment)}</td>
                          <td className={`text-right font-medium ${r.variance_aed < 0 ? 'text-red-600' : 'text-slate-900'}`}>
                            {r.final_boq_total
                              ? (r.variance_aed < 0
                                  ? `−${AED_short(Math.abs(r.variance_aed))} over`
                                  : AED_short(r.budget_remaining_aed))
                              : <span className="text-slate-400">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* BOQs grouped by vendor */}
          <div className="glass p-6">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <div className="font-serif text-2xl">Bill of Quantities</div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setBoqUploadOpen(true)}
                  data-testid="upload-boq-btn"
                  className="btn-primary text-sm inline-flex items-center gap-2">
                  <Upload size={14}/> Upload BOQ
                </button>
                <Link to={`/app/properties/${id}/boq-compare`} className="btn-ghost text-sm">Open comparison</Link>
              </div>
            </div>

            {boqs.length === 0 ? (
              <div className="text-slate-400 text-sm">No BOQs yet. Upload one to get started.</div>
            ) : (
              <div className="space-y-5">
                {Object.entries(
                  boqs.reduce((acc, b) => {
                    (acc[b.vendor_id] = acc[b.vendor_id] || []).push(b);
                    return acc;
                  }, {})
                ).map(([vid, group]) => {
                  const vendor = vendors.find(v => v.id === vid);
                  const finals = group.filter(b => b.is_final);
                  const finalsSum = finals.reduce((s, b) => s + (b.total_aed || 0), 0);
                  return (
                    <div key={vid} className="border-t border-slate-100/70 pt-4 first:border-t-0 first:pt-0">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div>
                          <div className="font-medium text-slate-900">{vendor?.company || vendor?.name || 'Unknown vendor'}</div>
                          <div className="text-xs text-slate-500">
                            {group.length} BOQ{group.length === 1 ? '' : 's'}
                            {finals.length > 0
                              ? <span className="ml-2 text-emerald-700">· {finals.length} finalised · Total {AED_short(finalsSum)}</span>
                              : <span className="ml-2 text-amber-700">· No final selected yet</span>}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {group.map(b => (
                          <div key={b.id}
                                data-testid={`boq-card-${b.id}`}
                                className={`p-4 rounded-2xl border transition-all ${
                                  b.is_final
                                    ? 'bg-emerald-50/60 border-emerald-300 shadow-inner'
                                    : 'bg-white/60 border-white/70 hover:border-slate-300'
                                }`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate">{b.title}</div>
                                <div className="text-xs text-slate-500 mt-1">
                                  Total: <span className="font-semibold">{AED(b.total_aed)}</span>
                                </div>
                                {b.negotiated_total_aed != null && b.line_sum_aed != null &&
                                  Math.abs((b.negotiated_total_aed || 0) - (b.line_sum_aed || 0)) > 1 && (
                                  <div className="text-[10px] text-amber-700 mt-0.5">
                                    Negotiated {AED_short(b.negotiated_total_aed)} · file sum {AED_short(b.line_sum_aed)}
                                  </div>
                                )}
                                {b.source_file_id && (
                                  <a href={fileUrl(b.source_file_id)} target="_blank" rel="noreferrer"
                                     data-testid={`boq-view-file-${b.id}`}
                                     className="inline-flex items-center gap-1 mt-1 text-[11px] text-[#679286] hover:underline">
                                    <ExternalLink size={10}/> View BOQ file
                                  </a>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-1 shrink-0">
                                {b.ai_fraud_score != null && (
                                  <span className={`status-pill ${b.ai_fraud_score >= 60 ? 'status-danger' : b.ai_fraud_score >= 30 ? 'status-warning' : 'status-success'}`}>
                                    Fraud {Math.round(b.ai_fraud_score)}
                                  </span>
                                )}
                                <button onClick={() => deleteBoq(b)}
                                        data-testid={`boq-delete-${b.id}`}
                                        title="Delete BOQ"
                                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-all">
                                  <Trash2 size={12}/>
                                </button>
                              </div>
                            </div>
                            {b.ai_flags?.length > 0 && (
                              <ul className="mt-3 text-xs text-slate-600 space-y-1 list-disc pl-4">
                                {b.ai_flags.slice(0, 2).map((f, i) => <li key={i} className="truncate">{f}</li>)}
                              </ul>
                            )}
                            <button
                              onClick={() => editNegotiatedTotal(b)}
                              data-testid={`boq-edit-total-${b.id}`}
                              className="w-full mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-500 hover:text-[#679286] transition-all">
                              {b.negotiated_total_aed != null
                                ? 'Edit negotiated total'
                                : 'Set negotiated total (override file sum)'}
                            </button>
                            {(!b.line_sum_aed || b.ai_fraud_score == null) && (
                              <button
                                onClick={() => analyzeBoq(b)}
                                disabled={analyzingBoqId === b.id}
                                data-testid={`boq-analyze-${b.id}`}
                                className="w-full mt-2 py-1.5 text-[11px] rounded-full bg-[#679286]/10 text-[#4a6d63] hover:bg-[#679286]/20 transition-all inline-flex items-center justify-center gap-1.5">
                                {analyzingBoqId === b.id
                                  ? <><Loader2 size={11} className="animate-spin"/> Analyzing…</>
                                  : <><Sparkles size={11}/> Analyze with Iris AI</>}
                              </button>
                            )}
                            <button
                              onClick={() => toggleFinal(b)}
                              disabled={markingId === b.id}
                              data-testid={`boq-mark-final-${b.id}`}
                              className={`w-full mt-3 pt-3 border-t text-xs flex items-center justify-center gap-2 transition-all ${
                                b.is_final
                                  ? 'border-emerald-200 text-emerald-700 hover:text-emerald-800'
                                  : 'border-slate-100 text-slate-600 hover:text-[#679286]'
                              }`}>
                              {markingId === b.id
                                ? <Loader2 size={12} className="animate-spin"/>
                                : b.is_final
                                  ? <><CheckCircle size={12}/> Final for this vendor · unmark</>
                                  : <>Mark as final for this vendor</>}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'Accounts' && (
        <div className="glass p-6">
          <div className="font-serif text-2xl mb-4">Invoices for this property</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="overline text-left border-b border-slate-200/60">
                <th className="py-2">Invoice</th><th>Description</th><th className="text-right">Amount</th><th>Status</th><th>Step</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} onClick={() => setViewInv(inv.id)} data-testid={`pd-inv-${inv.id}`}
                    className="border-b border-slate-100/70 cursor-pointer hover:bg-white/50">
                  <td className="py-3 font-mono text-xs">{inv.invoice_number}</td>
                  <td className="py-3">{inv.description}</td>
                  <td className="py-3 text-right">{AED(inv.amount_aed)}</td>
                  <td><span className={`status-pill ${inv.status === 'paid' ? 'status-success' : inv.status === 'rejected' ? 'status-danger' : 'status-warning'}`}>{inv.status?.replace(/_/g,' ')}</span></td>
                  <td className="capitalize text-xs text-slate-600">{inv.current_step?.replace('_', ' ')}</td>
                </tr>
              ))}
              {invoices.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No invoices.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'Drawings & Permits' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="glass p-6">
            <div className="font-serif text-xl mb-3">Drawings</div>
            {drawings.map(d => (
              <div key={d.id} className="flex items-center justify-between py-2 border-b border-slate-100/70 text-sm">
                <div><div className="font-medium">{d.title}</div><div className="text-xs text-slate-500">{d.discipline} · {d.revision} · {d.consultant}</div></div>
                <span className={`status-pill ${d.status === 'approved' ? 'status-success' : d.status === 'superseded' ? 'status-neutral' : 'status-warning'}`}>{d.status.replace('_',' ')}</span>
              </div>
            ))}
            {drawings.length === 0 && <div className="text-slate-400 text-sm">No drawings.</div>}
          </div>
          <div className="glass p-6">
            <div className="font-serif text-xl mb-3">Permits & submissions</div>
            {permits.map(pm => (
              <div key={pm.id} className="flex items-center justify-between py-2 border-b border-slate-100/70 text-sm">
                <div><div className="font-medium">{pm.authority}</div><div className="text-xs text-slate-500">{pm.permit_type} · {pm.reference_number}</div></div>
                <span className={`status-pill ${pm.status === 'approved' ? 'status-success' : pm.status === 'rejected' ? 'status-danger' : 'status-warning'}`}>{pm.status?.replace('_',' ')}</span>
              </div>
            ))}
            {permits.length === 0 && <div className="text-slate-400 text-sm">No permits.</div>}
          </div>
        </div>
      )}

      {tab === 'Documents' && (
        <div className="glass p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-serif text-2xl">Document vault</div>
            <label className="btn-primary text-sm flex items-center gap-2 cursor-pointer" data-testid="upload-doc-btn">
              <Upload size={14}/> Upload
              <input type="file" className="hidden" onChange={(e) => uploadDoc(e, 'general')} />
            </label>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {docs.map(d => (
              <a key={d.id} href={fileUrl(d.file_id)} target="_blank" rel="noreferrer"
                 className="p-3 bg-white/60 rounded-2xl border border-white/70 hover:shadow-md transition-all">
                <div className="flex items-center gap-2 mb-2">
                  {d.content_type?.startsWith('image') ? <ImageIcon size={16}/> : <FileText size={16}/>}
                  <div className="overline text-[0.6rem]">{d.category}</div>
                </div>
                <div className="text-xs font-medium truncate">{d.title}</div>
              </a>
            ))}
            {docs.length === 0 && <div className="text-slate-400 text-sm col-span-4">No documents.</div>}
          </div>
        </div>
      )}

      {tab === 'Photos' && <PhotosTab propertyId={id} photos={photos} onReload={load} />}
      {tab === 'Snags' && <SnagsTab propertyId={id} snags={snags} onReload={load} />}
      {tab === 'Sales' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <BuyersCard propertyId={id} buyers={buyers} onReload={load} />
          <ListingGenerator prop={p} />
        </div>
      )}
      {viewInv && <InvoiceModal invoiceId={viewInv} onClose={() => setViewInv(null)} onUpdate={load}/>}
      <BOQUploadModal
        open={boqUploadOpen}
        onClose={() => setBoqUploadOpen(false)}
        propertyId={id}
        onCreated={load}
      />
      <RentPropertyModal
        open={rentOpen}
        property={p}
        onClose={() => setRentOpen(false)}
        onDone={load}
      />
      <VendorLineReconModal
        open={!!lineRecon}
        onClose={() => setLineRecon(null)}
        propertyId={id}
        vendorId={lineRecon?.vendorId}
        vendorName={lineRecon?.vendorName}
      />
    </div>
  );
}

function YieldTile({ label, value, accent = 'text-slate-900', hint = '', testid }) {
  return (
    <div className="rounded-2xl bg-white/70 border border-white/70 p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-serif text-2xl mt-1 ${accent}`}>{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function ReconTile({ label, value, accent = 'text-slate-900', hint = '' }) {
  return (
    <div className="rounded-2xl bg-white/70 border border-white/70 p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-serif text-2xl mt-1 ${accent}`}>{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function PhotosTab({ propertyId, photos, onReload }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e, category) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const form = new FormData(); form.append('file', f); form.append('property_id', propertyId);
      form.append('category', category); form.append('title', f.name);
      await api.post('/files/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      onReload();
    } finally { setUploading(false); }
  };
  const before = photos.filter(p => p.category === 'before');
  const after = photos.filter(p => p.category === 'after');
  const progress = photos.filter(p => p.category === 'progress_photo');
  const Cat = ({ title, cat, items }) => (
    <div className="glass p-6" data-testid={`photos-${cat}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="font-serif text-xl">{title}</div>
        <label className="btn-primary text-xs flex items-center gap-1 cursor-pointer">
          <Upload size={12}/> Upload
          <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, cat)}/>
        </label>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {items.map(it => (
          <a key={it.id} href={fileUrl(it.file_id)} target="_blank" rel="noreferrer" className="aspect-square rounded-2xl overflow-hidden bg-slate-200 block">
            <img src={fileUrl(it.file_id)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform"/>
          </a>
        ))}
        {items.length === 0 && <div className="col-span-3 text-center text-sm text-slate-400 py-8 border border-dashed border-slate-300 rounded-2xl">No {title.toLowerCase()} yet</div>}
      </div>
    </div>
  );
  return (
    <div className="space-y-5">
      {uploading && <div className="text-sm text-slate-500">Uploading…</div>}
      <Cat title="Before" cat="before" items={before} />
      <Cat title="Progress log" cat="progress_photo" items={progress} />
      <Cat title="After" cat="after" items={after} />
    </div>
  );
}

function SnagsTab({ propertyId, snags, onReload }) {
  const [adding, setAdding] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState({ title: '', location: '', severity: 'medium', vendor_id: '', photo_file: null });
  useEffect(() => { api.get('/vendors').then(r => setVendors(r.data)); }, []);
  const submit = async (e) => {
    e.preventDefault();
    let photo_file_id = null;
    if (form.photo_file) {
      const fd = new FormData(); fd.append('file', form.photo_file);
      fd.append('property_id', propertyId); fd.append('category', 'snag_photo');
      fd.append('title', form.title);
      const r = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      photo_file_id = r.data.file_id;
    }
    await api.post('/snags', {
      property_id: propertyId, title: form.title, location: form.location,
      severity: form.severity, vendor_id: form.vendor_id || null, photo_file_id
    });
    setAdding(false); setForm({ title: '', location: '', severity: 'medium', vendor_id: '', photo_file: null });
    onReload();
  };
  const setStatus = async (id, status) => {
    await api.patch(`/snags/${id}`, { status });
    onReload();
  };
  const vMap = Object.fromEntries(vendors.map(v => [v.id, v]));
  const sev = { low: 'status-neutral', medium: 'status-warning', high: 'status-danger' };
  const st = { open: 'status-danger', in_progress: 'status-warning', resolved: 'status-success' };
  return (
    <div className="glass p-6" data-testid="snags-tab">
      <div className="flex items-center justify-between mb-4">
        <div className="font-serif text-2xl">Snagging register</div>
        <button onClick={() => setAdding(true)} data-testid="add-snag-btn" className="btn-primary text-sm flex items-center gap-2">
          <Plus size={14}/> Log snag
        </button>
      </div>
      <div className="space-y-2">
        {snags.map(s => (
          <div key={s.id} className="p-3 bg-white/60 rounded-2xl border border-white/70 flex items-center gap-3">
            {s.photo_file_id ? (
              <a href={fileUrl(s.photo_file_id)} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-xl overflow-hidden bg-slate-200 flex-shrink-0">
                <img src={fileUrl(s.photo_file_id)} alt="" className="w-full h-full object-cover"/>
              </a>
            ) : (
              <AlertTriangle size={20} className={s.severity === 'high' ? 'text-red-500' : s.severity === 'medium' ? 'text-amber-500' : 'text-slate-400'}/>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{s.title}</div>
              <div className="text-xs text-slate-500">{s.location} {s.vendor_id && vMap[s.vendor_id] && <>· Assigned to <span className="font-medium">{vMap[s.vendor_id].company}</span></>}</div>
            </div>
            <span className={`status-pill ${sev[s.severity]}`}>{s.severity}</span>
            <select value={s.status} onChange={(e) => setStatus(s.id, e.target.value)}
                    data-testid={`snag-status-${s.id}`}
                    className={`px-3 py-1 rounded-full text-xs border-0 ${st[s.status]}`}>
              <option value="open">Open</option>
              <option value="in_progress">In progress</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
        ))}
        {snags.length === 0 && <div className="text-center py-8 text-sm text-slate-400">No snags reported.</div>}
      </div>
      {adding && (
        <div className="fixed inset-0 z-40 grid place-items-center p-4 bg-slate-900/30 backdrop-blur-sm" onClick={() => setAdding(false)}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={submit} className="glass-strong w-full max-w-md p-6" data-testid="snag-modal">
            <div className="font-serif text-2xl mb-4">Log a snag</div>
            <label className="block text-sm mb-2">Title
              <input required value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} data-testid="snag-title"
                     className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
            </label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <label className="block text-sm">Location
                <input value={form.location} onChange={(e)=>setForm({...form, location:e.target.value})}
                       className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80" placeholder="Master bathroom"/>
              </label>
              <label className="block text-sm">Severity
                <select value={form.severity} onChange={(e)=>setForm({...form, severity:e.target.value})}
                        className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                </select>
              </label>
            </div>
            <label className="block text-sm mb-2">Assign to vendor
              <select value={form.vendor_id} onChange={(e)=>setForm({...form, vendor_id:e.target.value})} data-testid="snag-vendor"
                      className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
                <option value="">— None —</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.company} ({v.trade})</option>)}
              </select>
            </label>
            <label className="block text-sm mb-4">Photo (optional)
              <input type="file" accept="image/*" onChange={(e)=>setForm({...form, photo_file:e.target.files?.[0]})} data-testid="snag-photo"
                     className="w-full mt-1 text-xs"/>
            </label>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={()=>setAdding(false)} className="btn-ghost text-sm">Cancel</button>
              <button type="submit" className="btn-primary text-sm" data-testid="snag-save">Log snag</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function PartnersTab({ propertyId, partners, property, onReload }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', equity_pct: 50, contribution_aed: 0, role: 'investor' });
  const submit = async (e) => {
    e.preventDefault();
    await api.post('/partners', { ...form, property_id: propertyId });
    setAdding(false); setForm({ name: '', email: '', equity_pct: 50, contribution_aed: 0, role: 'investor' });
    onReload();
  };
  const removePartner = async (id) => {
    if (!confirm('Remove this partner?')) return;
    await api.delete(`/partners/${id}`);
    onReload();
  };
  const totalEquity = partners.reduce((s, p) => s + (p.equity_pct || 0), 0);
  const totalContrib = partners.reduce((s, p) => s + (p.contribution_aed || 0), 0);
  const totalDist = partners.reduce((s, p) => s + (p.distributed_aed || 0), 0);

  return (
    <div className="space-y-5" data-testid="partners-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="glass p-4"><div className="overline">Total contributed</div><div className="font-serif text-2xl mt-1">{AED(totalContrib)}</div></div>
        <div className="glass p-4"><div className="overline">Distributed to partners</div><div className="font-serif text-2xl mt-1 text-emerald-700">{AED(totalDist)}</div></div>
        <div className="glass p-4"><div className="overline">Equity assigned</div><div className={`font-serif text-2xl mt-1 ${totalEquity === 100 ? 'text-emerald-700' : totalEquity > 100 ? 'text-red-600' : 'text-amber-600'}`}>{totalEquity}%</div></div>
      </div>
      <div className="glass p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-serif text-2xl">Cap table</div>
          <button onClick={() => setAdding(true)} data-testid="add-partner-btn" className="btn-primary text-sm flex items-center gap-2"><Plus size={14}/> Add partner</button>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="overline text-left border-b border-slate-200/60"><th className="py-2">Partner</th><th>Email</th><th className="text-right">Equity</th><th className="text-right">Contributed</th><th className="text-right">Distributed</th><th></th></tr></thead>
          <tbody>
            {partners.map(p => (
              <tr key={p.id} className="border-b border-slate-100/70" data-testid={`partner-row-${p.id}`}>
                <td className="py-3">
                  <div className="font-medium">{p.name}</div>
                  <div className="overline text-[0.55rem]">{p.role}</div>
                </td>
                <td className="text-xs text-slate-500">{p.email}</td>
                <td className="text-right font-semibold">{p.equity_pct}%</td>
                <td className="text-right">{AED(p.contribution_aed)}</td>
                <td className="text-right text-emerald-700">{AED(p.distributed_aed)}</td>
                <td className="text-right">
                  <button onClick={() => removePartner(p.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
            {partners.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">No partners — this is a solo deal.</td></tr>}
          </tbody>
        </table>
      </div>

      {adding && (
        <div className="fixed inset-0 z-40 grid place-items-center p-4 bg-slate-900/30 backdrop-blur-sm" onClick={()=>setAdding(false)}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={submit} className="glass-strong w-full max-w-md p-6" data-testid="partner-modal">
            <div className="font-serif text-2xl mb-4">Add partner</div>
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-2 text-sm">Name
                <input required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="partner-name"
                       className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
              </label>
              <label className="col-span-2 text-sm">Email
                <input type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})}
                       className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
              </label>
              <label className="text-sm">Equity %
                <input type="number" min="0" max="100" required value={form.equity_pct} onChange={(e)=>setForm({...form, equity_pct:+e.target.value})}
                       className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
              </label>
              <label className="text-sm">Contribution (AED)
                <input type="number" value={form.contribution_aed} onChange={(e)=>setForm({...form, contribution_aed:+e.target.value})}
                       className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
              </label>
              <label className="col-span-2 text-sm">Role
                <select value={form.role} onChange={(e)=>setForm({...form, role:e.target.value})}
                        className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
                  <option value="investor">Investor</option>
                  <option value="sweat_equity">Sweat equity</option>
                </select>
              </label>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button type="button" onClick={()=>setAdding(false)} className="btn-ghost text-sm">Cancel</button>
              <button type="submit" className="btn-primary text-sm" data-testid="partner-save">Add partner</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function BuyersCard({ propertyId, buyers, onReload }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', offer_aed: 0, status: 'lead' });
  const submit = async (e) => {
    e.preventDefault();
    await api.post('/buyers', { ...form, property_id: propertyId });
    setAdding(false);
    setForm({ name: '', email: '', phone: '', offer_aed: 0, status: 'lead' });
    onReload();
  };
  return (
    <div className="glass p-6">
      <div className="flex items-center justify-between mb-3">
        <div className="font-serif text-2xl">Buyers & offers</div>
        <button onClick={() => setAdding(true)} data-testid="add-buyer-btn" className="btn-primary text-xs flex items-center gap-1"><Plus size={12}/> Add</button>
      </div>
      {buyers.map(b => (
        <div key={b.id} className="py-2 border-b border-slate-100/70 flex justify-between text-sm">
          <div><div className="font-medium">{b.name}</div><div className="text-xs text-slate-500">{b.email}</div></div>
          <div className="text-right"><div className="font-semibold">{AED_short(b.offer_aed)}</div><div className="overline text-[0.6rem]">{b.status}</div></div>
        </div>
      ))}
      {buyers.length === 0 && <div className="text-sm text-slate-400">No leads yet.</div>}
      {adding && (
        <div className="fixed inset-0 z-40 grid place-items-center p-4 bg-slate-900/30 backdrop-blur-sm" onClick={()=>setAdding(false)}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={submit} className="glass-strong w-full max-w-md p-6">
            <div className="font-serif text-2xl mb-4">Add buyer</div>
            {['name','email','phone'].map(k => (
              <label key={k} className="block text-sm mb-2 capitalize">{k}
                <input required={k==='name'} type={k==='email'?'email':'text'} value={form[k]} onChange={(e)=>setForm({...form, [k]:e.target.value})} data-testid={`buyer-${k}`}
                       className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
              </label>
            ))}
            <label className="block text-sm mb-2">Offer (AED)
              <input type="number" value={form.offer_aed} onChange={(e)=>setForm({...form, offer_aed:+e.target.value})}
                     className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
            </label>
            <label className="block text-sm mb-4">Status
              <select value={form.status} onChange={(e)=>setForm({...form, status:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
                {['lead','viewed','offered','negotiating','closed','lost'].map(s=><option key={s}>{s}</option>)}
              </select>
            </label>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={()=>setAdding(false)} className="btn-ghost text-sm">Cancel</button>
              <button type="submit" className="btn-primary text-sm" data-testid="buyer-save">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function ListingGenerator({ prop }) {
  const [text, setText] = useState('');
  const [questions, setQuestions] = useState([]);
  const [qaPairs, setQaPairs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [refining, setRefining] = useState(false);

  const generate = async () => {
    setBusy(true);
    setQaPairs([]);
    try {
      const r = await api.post('/sales/generate-listing', prop);
      setText(r.data.listing || '');
      setQuestions(r.data.clarifying_questions || []);
    } finally { setBusy(false); }
  };

  const refine = async (newPairs) => {
    setRefining(true);
    const combined = [...qaPairs, ...newPairs];
    try {
      const r = await api.post('/sales/generate-listing', { ...prop, qa_pairs: combined });
      setText(r.data.listing || '');
      setQuestions(r.data.clarifying_questions || []);
      setQaPairs(combined);
    } finally { setRefining(false); }
  };

  return (
    <div className="glass p-8 space-y-5" data-testid="listing-generator">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline flex items-center gap-2"><Sparkles size={12} className="text-[#679286]"/> Iris AI</div>
          <div className="font-serif text-2xl mt-1">Listing generator</div>
        </div>
        <button onClick={generate} disabled={busy} className="btn-gold text-sm flex items-center gap-2" data-testid="listing-generate-btn">
          {busy ? <><Loader2 size={14} className="animate-spin"/> Writing…</> : <><Sparkles size={14}/> {text ? 'Regenerate' : 'Generate'}</>}
        </button>
      </div>

      {text ? <div className="text-base text-slate-800 whitespace-pre-wrap leading-relaxed">{text}</div>
            : <div className="text-sm text-slate-500">Iris AI writes a Bayut-ready listing description with headline, description, features and call-to-action.</div>}

      {text && questions.length > 0 && (
        <AIRefiner
          questions={questions}
          onRefine={refine}
          refining={refining}
          refinedCount={qaPairs.length > 0 ? 1 : 0}
        />
      )}
    </div>
  );
}
